/*
  Ian - 2026-07-24 - PDP enhancements: require a size choice before Add to Cart.
  Additive only. Loaded on the product template via layout/theme.liquid.
  Companion styling lives in assets/ian-custom.css (search "PDP enhancements").

  Behaviour
  ---------
  - For each main <product-details> (quick view skipped), every NON-colour swatch
    group (the Size selector on Eribe's split products) is treated as required.
  - Envy pre-selects the first available variant, so on load we clear that size
    selection and show a soft "Please select" cue next to the Size label.
  - The Add to Cart button stays normal/clickable. If the shopper clicks it without
    a size, we block the add and THEN reveal "Please choose a size to continue" and
    highlight the size row. The message is not shown until that attempt.
  - Once a size is chosen, the message and the cue clear.

  We never disable the button ourselves and never force-enable it either — Envy's
  own sold-out handling is left untouched. Groups are re-queried live because Envy
  replaces those nodes when it re-renders on a variant change.
*/
(function () {
  'use strict';

  var MESSAGE_TEXT = 'Please choose a size to continue';
  var HINT_TEXT = 'Please select';
  var ERROR_CLASS = 'ian-size-gate--error';
  var MSG_CLASS = 'ian-size-gate__message';
  var HINT_CLASS = 'ian-size-gate__hint';

  function isQuickView(el) {
    return !!(el.closest && el.closest('[data-quick-view-product]'));
  }

  /* Required (size) swatch groups: non-colour <product-swatch> with 2+ options. */
  function getRequiredGroups(pd) {
    var groups = [];
    pd.querySelectorAll('product-swatch').forEach(function (sw) {
      if (sw.classList.contains('is-color-swatch')) return;
      if (sw.querySelectorAll('input[type="radio"]').length >= 2) groups.push(sw);
    });
    return groups;
  }

  function groupHasSelection(g) {
    return !!g.querySelector('input[type="radio"]:checked');
  }

  function allChosen(pd) {
    return getRequiredGroups(pd).every(groupHasSelection);
  }

  function clearGroupSelection(g) {
    g.querySelectorAll('input[type="radio"]:checked').forEach(function (r) { r.checked = false; });
    g.querySelectorAll('.swatch-element.active').forEach(function (el) { el.classList.remove('active'); });
  }

  function getForm(pd) {
    return pd.querySelector('product-form form');
  }

  function ensureMessage(form) {
    var wrapper = form.querySelector('.paymentButtonsWrapper');
    if (!wrapper || !wrapper.parentNode) return null;
    var msg = form.querySelector('.' + MSG_CLASS);
    if (!msg) {
      msg = document.createElement('div');
      msg.className = MSG_CLASS;
      msg.setAttribute('role', 'alert');
      msg.hidden = true;
      msg.textContent = MESSAGE_TEXT;
      wrapper.parentNode.insertBefore(msg, wrapper);
    }
    return msg;
  }

  /* Soft "Please select" cue next to the Size label while nothing is chosen. */
  function toggleHints(groups) {
    groups.forEach(function (g) {
      var wrap = g.querySelector('.option-label-wrapper');
      if (!wrap) return;
      var hint = wrap.querySelector('.' + HINT_CLASS);
      var need = !groupHasSelection(g);
      if (need && !hint) {
        hint = document.createElement('span');
        hint.className = HINT_CLASS;
        hint.textContent = HINT_TEXT;
        (wrap.querySelector('.header') || wrap).appendChild(hint);
      } else if (!need && hint) {
        hint.remove();
      }
    });
  }

  /* Keep cues in sync and clear the error message once a size is chosen.
     Does NOT reveal the error message — that only happens on a blocked attempt. */
  function syncState(pd) {
    var groups = getRequiredGroups(pd);
    if (!groups.length) return;
    toggleHints(groups);
    if (groups.every(groupHasSelection)) {
      var form = getForm(pd);
      if (form) {
        var msg = form.querySelector('.' + MSG_CLASS);
        if (msg) msg.hidden = true;
      }
      groups.forEach(function (g) { g.classList.remove(ERROR_CLASS); });
    }
  }

  /* Shown only when the shopper tries to add without choosing a size. */
  function showError(pd) {
    var form = getForm(pd);
    if (form) {
      var msg = ensureMessage(form);
      if (msg) msg.hidden = false;
    }
    var groups = getRequiredGroups(pd);
    var first = null;
    groups.forEach(function (g) {
      if (!groupHasSelection(g)) {
        g.classList.add(ERROR_CLASS);
        if (!first) first = g;
      }
    });
    if (first && first.scrollIntoView) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function process(pd) {
    if (isQuickView(pd)) return;
    var form = getForm(pd);
    if (!form) return;
    if (!getRequiredGroups(pd).length) return; // default-variant / accessory: no gate

    if (!pd.__ianGateInit) {
      pd.__ianGateInit = true;

      // Force an explicit choice: clear Envy's pre-selected size.
      getRequiredGroups(pd).forEach(clearGroupSelection);
      ensureMessage(form);

      pd.addEventListener('change', function (e) {
        if (e.target && e.target.matches && e.target.matches('input[type="radio"]')) {
          requestAnimationFrame(function () { syncState(pd); });
        }
      });

      // Block the add until a size is chosen; reveal the message only on that attempt.
      form.addEventListener('submit', function (e) {
        if (!allChosen(pd)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showError(pd);
        }
      }, true);

      var obs = new MutationObserver(function () {
        clearTimeout(pd.__ianSyncTimer);
        pd.__ianSyncTimer = setTimeout(function () { syncState(pd); }, 30);
      });
      obs.observe(pd, { childList: true, subtree: true });
      pd.__ianGateObs = obs;
    }

    syncState(pd);
  }

  function run() {
    document.querySelectorAll('product-details').forEach(process);
  }

  if (document.readyState !== 'loading') {
    run();
  } else {
    document.addEventListener('DOMContentLoaded', run);
  }
  window.addEventListener('load', run);
  document.addEventListener('shopify:section:load', run);
})();
